/**
 * Column Chart Renderer (Clustered + Stacked)
 *
 * Creates an amCharts 5 XYChart with ColumnSeries from a ColumnChartConfig.
 * The `stacked` parameter controls whether series stack on top of each other.
 */

import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';

import type { ColumnChartConfig } from '../../../../types/dashboard';
import { DASHBOARD_TOKENS } from '../tokens';
import { buildAmChartsNumberFormat } from '../formatters';

export function buildColumnChart(
  root: am5.Root,
  config: ColumnChartConfig,
  data: Record<string, unknown>[],
  stacked: boolean
): am5xy.XYChart {
  const chart = root.container.children.push(
    am5xy.XYChart.new(root, {
      panX: false,
      panY: false,
      wheelX: 'none',
      wheelY: 'none',
      layout: root.verticalLayout,
    })
  );

  const xRenderer = am5xy.AxisRendererX.new(root, {
    cellStartLocation: 0.1,
    cellEndLocation: 0.9,
    minGridDistance: 30,
  });
  xRenderer.labels.template.setAll({
    fontSize: DASHBOARD_TOKENS.fontSize,
    fill: am5.color(DASHBOARD_TOKENS.textSecondary),
  });

  xRenderer.grid.template.setAll({ location: 1 });

  const xAxis = chart.xAxes.push(
    am5xy.CategoryAxis.new(root, {
      categoryField: config.categoryField,
      renderer: xRenderer,
      tooltip: am5.Tooltip.new(root, {}),
    })
  );

  const yRenderer = am5xy.AxisRendererY.new(root, {});
  yRenderer.labels.template.setAll({
    fontSize: DASHBOARD_TOKENS.fontSizeSmall,
    fill: am5.color(DASHBOARD_TOKENS.textMuted),
  });

  const numberFormat = buildAmChartsNumberFormat(config.axes?.y?.format, config.axes?.y?.currency);

  const yAxis = chart.yAxes.push(
    am5xy.ValueAxis.new(root, {
      renderer: yRenderer,
      min: config.axes?.y?.min,
      numberFormat,
    })
  );

  const tooltipText = numberFormat
    ? `{name}, {categoryX}: {valueY.formatNumber("${numberFormat}")}`
    : '{name}, {categoryX}: {valueY}';

  config.series.forEach((seriesConfig, idx) => {
    const color = seriesConfig.color || DASHBOARD_TOKENS.palette[idx % DASHBOARD_TOKENS.palette.length];

    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: seriesConfig.name,
        xAxis,
        yAxis,
        valueYField: seriesConfig.valueField,
        categoryXField: config.categoryField,
        stacked,
        fill: am5.color(color),
        stroke: am5.color(color),
      })
    );

    series.columns.template.setAll({
      cornerRadiusTL: config.visual?.cornerRadius ?? DASHBOARD_TOKENS.cornerRadius,
      cornerRadiusTR: config.visual?.cornerRadius ?? DASHBOARD_TOKENS.cornerRadius,
      strokeOpacity: 0,
      width: am5.percent(85),
      tooltipText: seriesConfig.tooltip || tooltipText,
      tooltipY: 0,
    });

    if (config.bulletLabels?.show) {
      const isInside = config.bulletLabels!.position === 'inside';
      series.bullets.push(() => {
        return am5.Bullet.new(root, {
          locationY: isInside ? 0.5 : 1,
          sprite: am5.Label.new(root, {
            text: '{valueY}',
            fill: isInside
              ? root.interfaceColors.get('alternativeText')
              : am5.color(DASHBOARD_TOKENS.textPrimary),
            fontSize: DASHBOARD_TOKENS.fontSizeSmall,
            centerY: isInside ? am5.percent(50) : am5.percent(100),
            centerX: am5.percent(50),
            populateText: true,
          }),
        });
      });
    }

    series.data.setAll(data);
    series.appear();
  });

  xAxis.data.setAll(data);

  if (config.visual?.legend !== false && config.series.length > 1) {
    const legend = chart.children.push(
      am5.Legend.new(root, {
        centerX: am5.percent(50),
        x: am5.percent(50),
      })
    );
    legend.labels.template.setAll({
      fill: am5.color(DASHBOARD_TOKENS.textSecondary),
      fontSize: DASHBOARD_TOKENS.fontSizeSmall,
    });
    legend.data.setAll(chart.series.values);
  }

  if (config.visual?.cursor) {
    chart.set('cursor', am5xy.XYCursor.new(root, { behavior: 'none' }));
  }

  return chart;
}
